import { isEmpty } from "lodash-es";

import type { ITeamLeader, ITeamLeaders } from "@/teams/components/Team/Leaders.vue";
import type {
  IRequestPayloadCreateClub,
  IResponseAtlitUser,
  IResponseClub,
  IResponseClubMemberships,
  IResponseTeamAthleteData,
  IResponseTeamLeadersData,
  IResponseUserClubMembership,
  ITeam,
  ITeamAthleteItem,
  ITeamMember,
  ITeamMemberPermissions,
  ITeamOwner,
  ITeamUserManagementRole,
  ITeamUserRole,
  TGenders,
} from "~/types";
import { formatSeasonPayload, formatSeasonStringToDate } from "@/common/helpers/format-season";
import { isSuperAdmin } from "~/common/composables/is-feature-enabled";
import { getSportTypesWithStatSheetsForAthleteStats } from "~/common/helpers/get-available-stat-types";
import { TEAM_MANAGEMENT_ROLES, TEAM_USER_ROLE, TEAM_USER_STATUS } from "~/constants";

import type { TTeamSchema } from "../schemas/team.schema";
import type { TUnofficialTeamSchema } from "../schemas/unofficial-team.schema";
import { formatAthleteRoster } from "../../athletes/helpers/format-athlete";
import formatLeague from "../../leagues/helpers/format-league";
import formatOrganization from "../../organizations/helpers/format-organization";
import { TEAM_TYPES } from "../constants/teams";

const MEMBERSHIP_ROLE = {
  owner: TEAM_USER_ROLE.OWNER,
  admin: TEAM_USER_ROLE.ADMIN,
  coach: TEAM_USER_ROLE.COACH,
  viewer: TEAM_USER_ROLE.MEMBER,
  athlete: TEAM_USER_ROLE.ATHLETE,
} as const;
const MEMBERSHIP_STATUS = {
  joined: TEAM_USER_STATUS.ACTIVE,
  invited: TEAM_USER_STATUS.INVITED,
  pending: TEAM_USER_STATUS.PENDING,
} as const;

// Checks if a sport type is gender-specific (e.g., "lacrosse-men" vs "lacrosse-women" are different sports).
export function isSportTypeGenderSpecific(sportType: string): boolean {
  return sportType.includes("-men") || sportType.includes("-women");
}

export function getFullName(payload: { firstName?: string; middleName?: string; lastName?: string }): string {
  return [payload.firstName, payload.middleName, payload.lastName].filter(Boolean).join(" ") || "---";
}

function getTeamOrganization(club: IResponseClub) {
  if (club.relationships?.organizations) {
    return Array.isArray(club.relationships.organizations) ? club.relationships.organizations[0] : club.relationships.organizations;
  }

  return club.attributes.organization || club.relationships?.organization;
}

export function getUserRoleByMemberShip(membership: IResponseUserClubMembership): ITeamUserRole {
  if (membership.attributes.status === "pending") return TEAM_USER_ROLE.PENDING_MEMBER;
  else if (MEMBERSHIP_ROLE[membership.attributes.role]) return MEMBERSHIP_ROLE[membership.attributes.role];

  return TEAM_USER_ROLE.NOT_MEMBER;
}

export function formatTeamsData(data: IResponseClub[], included?: IResponseUserClubMembership[]) {
  const teams = data.map((club) => {
    const membership = included?.find((membership) => membership.attributes.clubId === club.id);

    return formatTeam(club, membership);
  });

  return teams;
}

export function formatTeamToClubPayload(team: Partial<TTeamSchema>): Partial<IResponseClub["attributes"]> {
  const payload: Partial<IResponseClub["attributes"]> = {
    name: team.name,
    shortName: team.name_short,
    logo: team.logo_file_key,
    gender: team.gender as TGenders,
    season: formatSeasonPayload(team.season),
  };

  if (team.logo_url === null) {
    payload.logo = null;
  }

  if (team.color_home || team.color_away) payload.jersey = { home: team.color_home, away: team.color_away };

  if (team.location) {
    payload.location = {
      country: team.location?.country,
      state: team.location?.state,
      city: team.location?.city,
    };
  }

  return payload;
}

export default function formatTeam(club: IResponseClub, userMembership?: IResponseUserClubMembership): ITeam {
  const canInviteViewer = club.meta?.permissions?.includes("members:invite:viewer") || false;
  const canInviteAdmin = club.meta?.permissions?.includes("members:invite:admin") || false;
  const canInviteCoach = club.meta?.permissions?.includes("members:invite:coach") || false;
  const availableInviteRoles = [canInviteAdmin && "admin", canInviteCoach && "coach", canInviteViewer && "viewer"].filter(Boolean);
  const membership = userMembership || (Array.isArray(club.relationships?.memberships) ? club.relationships?.memberships[0] : club.relationships?.memberships);

  const ownerUser = !isEmpty(club.relationships?.owner) ? formatTeamOwner(club.relationships.owner) : undefined;

  const userRole = membership ? getUserRoleByMemberShip(membership) : TEAM_USER_ROLE.NOT_MEMBER;
  const sportType = club.attributes.sportType;
  const isTeamManagementRole = TEAM_MANAGEMENT_ROLES.includes(userRole as ITeamUserManagementRole);
  const organization = getTeamOrganization(club);

  const hasEventsUploadPermission = club.meta?.permissions?.includes("events:upload") || false;
  const canUploadVideo = !!(isFeatureEnabled("enable_team_video_upload") && (hasEventsUploadPermission || isSuperAdmin()));

  const team: ITeam = {
    id: club.id,
    vidswapId: club.attributes.external?.vidswap,
    type: club.attributes.type,
    name: club.attributes.name,
    name_short: club.attributes.shortName,
    logo_url: club.attributes.logo,
    sport_type: sportType,
    gender: club.attributes.gender,
    ownerUser,
    level: club.attributes.level as ITeam["level"],
    isOfficialTeam: club.attributes.isOfficialTeam,
    fallbackSeason: {
      // @ts-expect-error TODO: Should be added in the `atlit-be` swagger definition
      format: club.attributes.fallbackSeason?.format,
      // @ts-expect-error TODO: Should be added in the `atlit-be` swagger definition
      startDate: formatSeasonStringToDate(club.attributes.fallbackSeason?.startDate),
    },
    season: {
      format: club.attributes.season?.format,
      startDate: formatSeasonStringToDate(club.attributes.season?.startDate),
    },
    color_home: club.attributes.jersey?.home,
    color_away: club.attributes.jersey?.away,
    user_role: userRole,
    location: {
      country: club.attributes.location?.country,
      state: club.attributes.location?.state,
      city: club.attributes.location?.city,
    },
    basic_assigned: club.relationships?.breakdowns?.basic?.assigned || 0,
    advanced_assigned: club.relationships?.breakdowns?.advanced?.assigned || 0,
    seasonsHistoryMap: new Map(),
    permissions: {
      canRead: club.meta?.permissions?.includes("read") || false,
      canWrite: club.meta?.permissions?.includes("write") || false,
      canReadMembers: club.meta?.permissions?.includes("members:read") || false,
      canEditMembers: club.meta?.permissions?.includes("write") || false,
      canInvite: (role) => club.meta?.permissions?.includes(`members:invite:${role}`) || false,
      canLeave: club.meta?.permissions?.includes("leave") || false,
      canDelete: club.meta?.permissions?.includes("delete") || false,
      canOpenDeepLinkRoster: (isFeatureEnabled("enable_team_manage_rosters_vs_deep_link") && club.meta?.permissions?.includes("deeplink:roster")) || false,
      canOpenDeepLinkHome: club.meta?.permissions?.includes("deeplink:home") || false,
      availableStats: club.meta?.permissions?.includes("stats:overview") || false,
      availableInviteRoles: availableInviteRoles as ITeamUserRole[],
      canPurchaseBreakdowns: isTeamManagementRole,
      canSendBreakdowns: userRole === TEAM_USER_ROLE.OWNER,
      canReadAthletes: club.meta?.permissions?.includes("athletes:read") || false,
      canOpenOverviewTab: isFeatureEnabled("enable_team_overview_tab") || false,
      canOpenTeamStatsTab: (isFeatureEnabled("enable_team_stats_tab") && club.meta?.permissions?.includes("stats:overview")) || false,
      canOpenRosterStatsTab:
        (isFeatureEnabled("enable_team_roster_stats_tab") &&
          getSportTypesWithStatSheetsForAthleteStats().includes(sportType) &&
          club.meta?.permissions?.includes("stats:overview")) ||
        false,
      canImportVideo: !!(isFeatureEnabled("enable_team_video_import") && isSuperAdmin()),
      canOpenReplay: !!(isFeatureEnabled("enable_team_video_import_replay") && club.meta?.permissions?.includes("events:import:replay")),
      canUploadVideo,
      canUploadScoutVideo: canUploadVideo && isFeatureEnabled("enable_team_video_upload_scout"),
      canEditAthletes: club.meta?.permissions?.includes("athletes:write") || false,
      canGrantAccess: club.meta?.permissions?.includes("athlete:access:restore") || false,
      canRevokeAccess: club.meta?.permissions?.includes("athlete:access:revoke") || false,
      canDeleteAthlete: club.meta?.permissions?.includes("athlete:delete") || false,
      availableTeamLeaders: isFeatureEnabled("enable_team_leaders"),
      availableTeamAthletes: isFeatureEnabled("enable_team_athletes_tab") || false,
    },
  };

  if (organization) {
    team.organization = formatOrganization(organization);
  }

  if (club.relationships?.leagueclubs) {
    if (Array.isArray(club.relationships.leagueclubs)) {
      club.relationships.leagueclubs.forEach((cl) => {
        if (!cl.attributes.league) return;
        const league = formatLeague(cl.attributes.league);
        cl.attributes.seasons.forEach((s) => {
          team.seasonsHistoryMap?.set(s, league);
        });
      });
    } else {
      [club.relationships.leagueclubs].forEach((cl) => {
        if (!cl.attributes.league) return;
        const league = formatLeague(cl.attributes.league);
        cl.attributes.seasons.forEach((s) => {
          team.seasonsHistoryMap?.set(s, league);
        });
      });
    }
  }

  if (club.relationships?.rosterAthlete) {
    team.athleteRoster = formatAthleteRoster(club.relationships?.rosterAthlete);
  }

  return team;
}

export function formatTeamMembersData(res: IResponseClubMemberships): ITeamMember[] {
  if (!res.included) return [];

  const members = res.data.map((membership) => {
    const user = res.included?.find((user) => user.id === membership.attributes.userId);

    return formatTeamMember(membership, user);
  });

  return members;
}

export function getPermissionsByMembership(membership: IResponseUserClubMembership): ITeamMemberPermissions {
  return {
    canWrite: membership.meta?.permissions?.includes("write") || false,
    canRead: membership.meta?.permissions?.includes("read") || false,
    canSetAthlete: membership.meta?.permissions?.includes("set:athlete") || false,
    canSetViewer: membership.meta?.permissions?.includes("set:viewer") || false,
    canSetAdmin: membership.meta?.permissions?.includes("set:admin") || false,
    canSetCoach: membership.meta?.permissions?.includes("set:coach") || false,
    canSetOwner: membership.meta?.permissions?.includes("set:owner") || false,
    canDelete: membership.meta?.permissions?.includes("delete") || false,
  };
}

export function formatTeamOwner(user: IResponseAtlitUser): ITeamOwner {
  return {
    uid: user.attributes?.uid,
    atlitUserId: user.id,
    email: user.attributes?.email || "-",
    fullName: user.attributes?.fullName || "-",
  };
}

export function formatTeamMember(membership: IResponseUserClubMembership, user?: IResponseAtlitUser): ITeamMember {
  return {
    uid: user?.attributes.uid,
    atlitUserId: user?.id,
    membershipId: membership.id,
    athleteId: membership.attributes.athleteId,
    email: user?.attributes.email || membership.attributes.profile?.email || "-",
    fullName: user?.attributes.fullName || getFullName(membership.attributes.profile || {}),
    hasSuperAdminRole: user?.attributes?.isSuperAdmin || false,
    user_role: getUserRoleByMemberShip(membership),
    user_status: MEMBERSHIP_STATUS[membership.attributes.status],
    permissions: getPermissionsByMembership(membership),
  };
}

export function formatTeamCreatePayload(payload: TTeamSchema | TUnofficialTeamSchema): IRequestPayloadCreateClub {
  return {
    type: payload.type || TEAM_TYPES.PRIMARY,
    name: payload.name,
    shortName: payload.name_short || undefined,
    sportType: payload.sport_type,
    logo: payload.logo_file_key || undefined,
    jersey: {
      home: payload.color_home,
      away: payload.color_away,
    },
    gender: payload.gender as TGenders,
    location: payload.location
      ? {
          country: payload.location.country,
          state: payload.location.state || undefined,
          city: payload.location.city || undefined,
        }
      : undefined,
    season: formatSeasonPayload(payload.season),
  };
}

export function formatAthleteRosterItem(item: IResponseTeamAthleteData): ITeamAthleteItem {
  const status = item.attributes?.status;
  const inviteStatus = item.relationships.membership?.attributes?.status as ITeamAthleteItem["inviteStatus"];
  // If inviteStatus is "declined", treat as inactive
  const effectiveStatus = inviteStatus !== "declined" ? status : "inactive";

  return {
    id: item.id,
    athleteId: item.attributes?.athleteId,
    name: getFullName(item.relationships.membership?.attributes?.profile || {}),
    email: item.relationships.membership?.attributes?.profile?.email,
    season: item.attributes?.roster?.season,
    status,
    inviteStatus,
    effectiveStatus,
    jersey: item.attributes?.jersey,
    positions: item.attributes?.positions,
    logo_url: item.relationships.membership?.attributes.profile?.logo,
    membership: item.relationships.membership,
    deleted: item.relationships.membership?.attributes.deleted || false,
    permissions: {
      canWrite: item.meta?.permissions?.includes("write") || false,
    },
  };
}

export const formatFlowBasedTeamLeaders = (teamLeaders: IResponseTeamLeadersData) => {
  const data: ITeamLeader[] = [];

  for (const category in teamLeaders.athleteStats.results.allStats[0].stats) {
    const categoryMetrics = teamLeaders.athleteStats.results.allStats[0].stats[category] as Record<string, number>;
    const categoryData: ITeamLeader[] = [];

    const entries = Object.entries(categoryMetrics);
    entries.sort((a, b) => b[1] - a[1]);
    const [leaderId, points] = entries[0];
    const leader = teamLeaders.athletes[leaderId];
    const metric = teamLeaders.athleteStats.metrics[category];

    categoryData.push({
      id: category,
      metric: metric.name,
      athlete_first_name: leader?.relationships?.athlete?.attributes?.firstName ?? "",
      athlete_last_name: leader?.relationships?.athlete?.attributes?.lastName ?? "",
      athlete_logo: leader?.relationships?.athlete?.attributes?.logo ?? "",
      points,
    });

    data.push(...categoryData);
  }

  return data.slice(0, 4);
};

export const formatPlayBasedTeamLeaders = (teamLeaders: IResponseTeamLeadersData) => {
  const data: ITeamLeaders = {};

  for (const category in teamLeaders.athleteStats.results.allStats[0].stats) {
    const categoryMetrics = teamLeaders.athleteStats.results.allStats[0].stats[category] as Record<string, number | Record<string, number>>;
    const categoryData: ITeamLeader[] = [];

    for (const metric in categoryMetrics) {
      const metricData = teamLeaders.athleteStats.metrics[metric];
      const leaderId = Object.entries(categoryMetrics[metric]).sort((a, b) => b[1] - a[1])?.[0]?.[0];
      if (!leaderId) break;
      const leader = teamLeaders.athletes[leaderId];

      categoryData.push({
        id: leaderId,
        metric: metricData.name,
        athlete_first_name: leader?.relationships?.athlete?.attributes?.firstName ?? "",
        athlete_last_name: leader?.relationships?.athlete?.attributes?.lastName ?? "",
        athlete_logo: leader?.relationships?.athlete?.attributes?.logo ?? "",
        points: Number((categoryMetrics[metric] as Record<string, number>)[leaderId]),
      });
    }

    if (!categoryData.length) break;

    data[category] = categoryData;
  }

  return data;
};
